import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { createTokenizeController } from './nmiTokenizeController';

/**
 * NmiCardForm — PCI-safe card entry for NMI / LuQra using Collect.js.
 *
 * Collect.js renders NMI-hosted iframes into our placeholder divs. The card
 * number and CVV are typed into NMI's iframes and posted directly to NMI, which
 * returns a single-use `payment_token`. Raw card data never touches the portal
 * frontend state, the portal backend, or the portal database.
 *
 * This is the NMI analogue of Stripe's <CardElement> and is only mounted when
 * the active processor is NMI. The Stripe path is completely untouched.
 *
 * Usage:
 *   const cardRef = useRef();
 *   <NmiCardForm ref={cardRef} tokenizationKey={key} onError={setCardError} />
 *   const token = await cardRef.current.tokenize();   // throws on failure
 */

const COLLECT_JS_URL = 'https://secure.nmi.com/token/Collect.js';

function loadCollectJs(tokenizationKey) {
  return new Promise((resolve, reject) => {
    if (window.CollectJS) return resolve(window.CollectJS);

    const existing = document.querySelector(`script[src="${COLLECT_JS_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.CollectJS));
      existing.addEventListener('error', () => reject(new Error('Failed to load payment library')));
      return;
    }

    const script = document.createElement('script');
    script.src = COLLECT_JS_URL;
    script.async = true;
    // Public tokenization key — safe in the browser. It can mint tokens but
    // cannot move money, read transactions, or issue refunds.
    script.setAttribute('data-tokenization-key', tokenizationKey);
    script.onload = () => resolve(window.CollectJS);
    script.onerror = () => reject(new Error('Failed to load payment library'));
    document.head.appendChild(script);
  });
}

const NmiCardForm = forwardRef(function NmiCardForm({ tokenizationKey, onError }, ref) {
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // One controller per mount. It guarantees tokenize() always settles, which
  // startPaymentRequest() alone does not: Collect.js aborts on validation
  // failure and then calls neither `callback` nor `timeoutCallback`.
  const ctlRef = useRef(null);
  if (!ctlRef.current) ctlRef.current = createTokenizeController();

  useEffect(() => {
    let cancelled = false;
    if (!tokenizationKey) {
      setLoadError('Card payments are not configured.');
      return undefined;
    }

    loadCollectJs(tokenizationKey)
      .then((CollectJS) => {
        if (cancelled || !CollectJS) return;
        CollectJS.configure({
          variant: 'inline',
          styleSniffer: false,
          // Field styling kept deliberately close to the Stripe CardElement so
          // the checkout looks the same to patients regardless of processor.
          // NOTE: do NOT use font-family:'inherit' here. Collect.js renders these
          // fields inside its own cross-origin iframe, where 'inherit' has no
          // parent to resolve against. Text metrics then collapse, Collect.js
          // measures a zero content height, and it writes an inline
          // `height: 0px` onto the iframe that overrides its own height="22px"
          // attribute. The result is a field that LOOKS correct (the container
          // div keeps its height) but is 0px tall, so it cannot be clicked,
          // focused, or typed into. Always give an explicit font stack and
          // explicit line-height/height so the measurement is non-zero.
          customCss: {
            'font-size': '16px',
            'font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            'line-height': '22px',
            height: '22px',
            color: '#2c2c2c',
            padding: '10px 12px',
          },
          invalidCss: { color: '#b3261e' },
          validCss: { color: '#2c2c2c' },
          fields: {
            ccnumber: { selector: '#nmi-ccnumber', placeholder: 'Card number' },
            ccexp: { selector: '#nmi-ccexp', placeholder: 'MM / YY' },
            cvv: { selector: '#nmi-cvv', placeholder: 'CVC' },
          },
          validationCallback: (field, valid, message) => {
            if (!valid && onError) onError(message || `Invalid ${field}`);
            else if (valid && onError) onError(null);
            // THE FIX: end a pending tokenize instead of leaving it hanging.
            ctlRef.current.onValidation(field, valid, message);
          },
          timeoutDuration: 15000,
          timeoutCallback: () => { ctlRef.current.onTimeout(); },
          fieldsAvailableCallback: () => { if (!cancelled) setReady(true); },
          callback: (response) => { ctlRef.current.onCallback(response); },
        });
      })
      .catch((e) => { if (!cancelled) setLoadError(e.message); });

    // Closing the modal mid-verification must not strand the promise.
    return () => { cancelled = true; ctlRef.current.cancel(); };
  }, [tokenizationKey, onError]);

  useImperativeHandle(ref, () => ({
    isReady: () => ready,
    /** Resolves with a single-use NMI payment token, or throws. Never hangs. */
    tokenize: () => {
      if (!window.CollectJS) return Promise.reject(new Error('Payment library not loaded'));
      if (!ready) {
        return Promise.reject(new Error('The card form is still loading. Please wait a moment and try again.'));
      }
      return ctlRef.current.start(() => window.CollectJS.startPaymentRequest());
    },
    /** Exposed for tests and for callers that abandon a verification. */
    cancel: (reason) => ctlRef.current.cancel(reason),
  }), [ready]);

  if (loadError) {
    return <div style={{ color: '#b3261e', fontSize: '14px' }}>{loadError}</div>;
  }

  const fieldStyle = {
    border: '1px solid var(--cream-dark, #ddd)',
    borderRadius: '6px',
    background: '#fff',
    height: '44px',
    display: 'block',
    width: '100%',
  };

  return (
    <div>
      {!ready && <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Loading secure card form…</div>}
      {/* Defensive floor: if Collect.js ever measures a zero content height again
          it writes an inline `height:0px` on the iframe, producing a field that
          renders but cannot be typed into. A min-height keeps the iframe
          clickable regardless of what Collect.js measures. !important is
          required because the offending value is set inline by Collect.js. */}
      <style>{`
        #nmi-ccnumber iframe, #nmi-ccexp iframe, #nmi-cvv iframe {
          min-height: 42px !important;
          height: 42px !important;
          width: 100% !important;
          border: 0;
          display: block !important;
        }
      `}</style>
      <div style={{ display: ready ? 'block' : 'none' }}>
        <div id="nmi-ccnumber" style={{ ...fieldStyle, marginBottom: '10px' }} />
        <div style={{ display: 'flex', gap: '10px' }}>
          <div id="nmi-ccexp" style={{ ...fieldStyle, flex: 1 }} />
          <div id="nmi-cvv" style={{ ...fieldStyle, flex: 1 }} />
        </div>
      </div>
    </div>
  );
});

export default NmiCardForm;
